import { useMemo } from 'react';
import { TableColumn } from '@components/TableNew/TableColumns';
import { baseSizeTitleMap, ByoTableColumn, ByoTableRow } from '@containers/ByoTableNewContainer/ByoTable/byoTable.helpers';
import { ByoAnalyticType } from '@pages/Byo/ByoContext';
import { getMacStageName } from '@models/MacStage';

export type ByoTableConfigInput = {
    rows: ByoTableRow[];
    columns: ByoTableColumn[];
    activeAnalyticsType: ByoAnalyticType;
};

export function useByoTableConfig({
    rows,
    columns,
    activeAnalyticsType,
}: ByoTableConfigInput) {
    const keywordNameCell: Omit<TableColumn, 'Value'> = useMemo(() => ({
        key: 'keywordName',
        head: '',
        headText: '',
        footer: baseSizeTitleMap[activeAnalyticsType],
        footerText: baseSizeTitleMap[activeAnalyticsType],
        valueText: (index) => {
            const keyword = rows[index];
            return keyword?.name?.toUpperCase() ?? '';
        },
    }), [
        activeAnalyticsType,
        rows,
    ]);

    const macStageCell: Omit<TableColumn, 'Value'> = useMemo(
        () => ({
            key: 'macStage',
            sortKey: 'macStage',
            head: 'MAC',
            headText: 'MAC',
            footer: '',
            footerText: '',
            valueText: (index) => {
                const rowData = rows[index];
                return rowData?.macStage ? getMacStageName(rowData.macStage) : '-';
            },
        }),
        [rows]
    );

    const dataTableColumns: Omit<TableColumn, 'Value'>[] = useMemo(
        () => {
            return columns.map((column, columnIndex) => {
                return {
                    key: column.key,
                    head: '',
                    headText: String(column.name),
                    footer: column.footerContent || '',
                    footerText: column.footerContent || '',
                    sortKey: column.sortKey,
                    valueText: (index) => {
                        const rowData = rows[index];
                        const cellValue = rowData?.columnsValues?.[columnIndex];
                        return String(cellValue ?? '-');
                    },
                };
            });
        },
        [columns, rows]
    );

    return [keywordNameCell, macStageCell].concat(dataTableColumns)
}
